'use client';
import React, { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

// ── Translation map ─────────────────────────────────────────────────────────
const translations: Record<string, Record<string, string>> = {
  EN: {
    home: 'Home', howItWorks: 'How It Works', about: 'About Us', contact: 'Contact',
    insurance: 'Insurance',
    signIn: 'Sign In', getStarted: 'Get Started Free', language: 'Language',
  },
  SW: {
    home: 'Nyumbani', howItWorks: 'Jinsi Inavyofanya Kazi', about: 'Kuhusu Sisi', contact: 'Wasiliana',
    insurance: 'Bima',
    signIn: 'Ingia', getStarted: 'Anza Bure', language: 'Lugha',
  },
  AR: {
    home: 'الرئيسية', howItWorks: 'كيف يعمل', about: 'من نحن', contact: 'اتصل بنا',
    insurance: 'التأمين',
    signIn: 'تسجيل الدخول', getStarted: 'ابدأ مجاناً', language: 'اللغة',
  },
  FR: {
    home: 'Accueil', howItWorks: 'Comment ça marche', about: 'À propos', contact: 'Contact',
    insurance: 'Assurance',
    signIn: 'Se connecter', getStarted: 'Commencer gratuitement', language: 'Langue',
  },
  HI: {
    home: 'होम', howItWorks: 'यह कैसे काम करता है', about: 'हमारे बारे में', contact: 'संपर्क',
    insurance: 'बीमा',
    signIn: 'साइन इन', getStarted: 'मुफ्त शुरू करें', language: 'भाषा',
  },
};

const languages = [
  { code: 'EN', label: 'English', flag: '🇬🇧' },
  { code: 'SW', label: 'Kiswahili', flag: '🇰🇪' },
  { code: 'AR', label: 'العربية', flag: '🇸🇦' },
  { code: 'FR', label: 'Français', flag: '🇫🇷' },
  { code: 'HI', label: 'हिन्दी', flag: '🇮🇳' },
];

export default function PublicNavbar() {
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(languages[0]);

  const t = translations[currentLang.code] ?? translations['EN'];

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-lang-selector]')) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  const selectLang = useCallback((lang: typeof languages[0]) => {
    setCurrentLang(lang);
    setLangOpen(false);
  }, []);

  const navLinks = [
    { label: t.home, href: '/' },
    { label: t.howItWorks, href: '/process-guide' },
    { label: 'Couriers', href: '/browse-couriers' },
    { label: t.about, href: '/about' },
    { label: t.contact, href: '/contact' },
  ];

  return (
    <nav
      dir="ltr"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-[0_2px_24px_rgba(15,23,42,0.12)] border-b border-slate-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <AppLogo size={40} variant={scrolled ? 'default' : 'white'} className="group-hover:opacity-80 transition-opacity" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  scrolled
                    ? 'text-slate-700 hover:text-[#16A34A] hover:bg-green-50'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA buttons + Language switcher */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language selector */}
            <div className="relative" data-lang-selector>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  scrolled
                    ? 'text-slate-600 hover:bg-slate-100 border border-slate-200' :'text-white/80 hover:text-white border border-white/20 hover:bg-white/10'
                }`}
                aria-label="Select language"
                dir="ltr"
              >
                <span>{currentLang.flag}</span>
                <span className="font-mono text-xs">{currentLang.code}</span>
                <svg className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50" dir="ltr">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => selectLang(lang)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                        currentLang.code === lang.code
                          ? 'bg-green-50 text-[#16A34A] font-semibold'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      dir="ltr"
                    >
                      <span className="text-base flex-shrink-0">{lang.flag}</span>
                      <span className="flex-1">{lang.label}</span>
                      {currentLang.code === lang.code && (
                        <svg className="w-3.5 h-3.5 text-[#16A34A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              href="/sign-up-login-screen"
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                scrolled
                  ? 'text-[#16A34A] hover:bg-green-50 border border-green-200'
                  : 'text-white/90 hover:text-white border border-white/30 hover:bg-white/10'
              }`}
            >
              {t.signIn}
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="px-5 py-2 rounded-lg text-sm font-bold font-heading bg-[#16A34A] text-white hover:bg-[#15803D] transition-all duration-200 shadow-[0_4px_14px_rgba(22,163,74,0.35)] hover:shadow-[0_6px_20px_rgba(22,163,74,0.45)] hover:-translate-y-0.5"
            >
              {t.getStarted}
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white/98 backdrop-blur-xl rounded-2xl mb-4 shadow-xl border border-slate-200 overflow-hidden" dir="ltr">
            <div className="p-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm font-semibold text-slate-700 hover:text-[#16A34A] hover:bg-green-50 transition-colors"
                >
                  {link.label}
                </Link>
              ))}

              {/* Mobile language switcher */}
              <div className="pt-2 border-t border-slate-100">
                <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">{t.language}</p>
                <div className="grid grid-cols-3 gap-1 px-1 pb-1">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => selectLang(lang)}
                      dir="ltr"
                      className={`flex flex-col items-center gap-1 py-2 px-1 rounded-xl text-xs font-semibold transition-colors ${
                        currentLang.code === lang.code
                          ? 'bg-green-50 text-[#16A34A] border border-green-200'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.code}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
                <Link
                  href="/sign-up-login-screen"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center px-4 py-3 rounded-xl text-sm font-semibold text-[#16A34A] border border-green-200 hover:bg-green-50 transition-colors"
                >
                  {t.signIn}
                </Link>
                <Link
                  href="/sign-up-login-screen"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center px-4 py-3 rounded-xl text-sm font-bold font-heading bg-[#16A34A] text-white hover:bg-[#15803D] transition-colors"
                >
                  {t.getStarted}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
